// Report uncaught JS errors to Haystack
//   Stolen from .com master (ddba31f59504869c)
//   https://github.com/github/github/blob/ddba31f59504869c19db1fbde6a00faabd061f43/app/assets/javascripts/github/failbot.coffee
//
//   https://haystack.github.com/render-js

import {isDefined} from './utils'
import {parse} from 'stacktrace-parser'

const defaultContext = {originalHistoryState: JSON.stringify(window.history.state)}
const ERROR_METADATA_KEYS = ['commit', 'repository_id', 'repository_type', 'browser', 'version']

/**
 * Firefox (and perhaps other browsers) will ocassionally return a null
 * value for the error property on an error event.
 * It is unclear why this would happen, but it leads to some confusing error reporting
 * in Sentry
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/GlobalEventHandlers/onerror
 */
const errorFallback = {
  name: 'NullErrorEvent',
  stack: ''
}

/**
 * Parses the url of the renderable being displayed for metadata that can help us
 * troubleshoot client JS errors without exposing PII.
 * This data is currently being proxied to failbotg from the go server.
 *
 * @param {Object} locationData object adhering to the Location interface
 * @returns Map of additional error data we want to supply to sentry
 */
function getErrorMetadata(locationData) {
  const {pathname, search} = locationData
  const pathParts = pathname.split('/')
  const [action, format] = pathParts.slice(1)
  const urlParams = new URLSearchParams(search)

  const paramsMetadata = Array.from(urlParams.entries()).reduce((memo, pair) => {
    if (ERROR_METADATA_KEYS.includes(pair[0])) {
      memo = {
        ...memo,
        [pair[0]]: pair[1]
      }
    }

    return memo
  }, {})

  return {...paramsMetadata, format, action}
}

/**
 * Many errors are emitted from chrome extensions, e.g. users
 * with TexAllTheThings, which attempts to load a version of mathjax.
 * That extension doesn't handle load failures and results in errors
 * being reported to us that we:
 * * cannot control
 * * do not affect the rendering of the notebook
 * So, we ignore them when detected.
 * @param {string} filename
 * @returns boolean
 */
function isChromeExtension(filename = '') {
  return filename.includes('chrome-extension://')
}

let contexts = []
const loadTime = new Date().getTime()

// Flag when page is unloaded
let unloaded = false

function flagUnloaded() {
  unloaded = true
}

function flagLoaded() {
  unloaded = false
}

$(window).on('pageshow', flagLoaded)
$(window).on('pagehide', flagUnloaded)
$(window).on('error', reportErrors)

function reportErrors(event) {
  // Do this first so we can skip all the extra, unnecessary operations
  if (!reportable(event) && event.type !== 'submit') {
    return
  }
  const {message, filename, lineno, error} = event.originalEvent
  const safeError = error || errorFallback
  const {stack, name} = safeError
  const preprocessedStack = parse(stack)
  const stacktrace = preprocessedStack
    .map(frame => ({
      filename: frame.file || '',
      function: String(frame.methodName),
      lineno: (frame.lineNumber || 0).toString(),
      colno: (frame.column || 0).toString()
    }))
    .reverse()
  const exceptionDetail = [
    {
      type: name,
      value: message,
      stacktrace
    }
  ]

  const context = $.extend(
    {},
    defaultContext,
    ...contexts,
    {
      message,
      filename,
      lineno,
      url: window.location.href,
      readyState: document.readyState,
      referrer: document.referrer,
      stack: error != null ? error.stack : undefined,
      historyState: JSON.stringify(window.history.state),
      timeSinceLoad: Math.round(new Date().getTime() - loadTime),
      extensionScripts: JSON.stringify(extensionScripts().sort()),
      navigations: JSON.stringify(getNavigations()),
      exceptionDetail,
      platform: 'javascript',
      ...getErrorMetadata(window.location)
    },
    error != null ? error.failbotContext : undefined
  )
  contexts = []

  // Send captured error event (used by Quick Issue)
  $(document).trigger('captured:error', context)

  // Attach any logging info we have
  context.logging = (() => {
    try {
      const debugMessages = (isDefined(window.debug) ? window.debug.buffer() : undefined) || []
      return JSON.stringify(debugMessages)
    } catch (error1) {
      return
    }
  })()

  // Report errors to app
  const renderUrl = $('body').data('render-url')
  $.ajax({
    type: 'POST',
    url: `${renderUrl}/_errors`,
    data: JSON.stringify(context),
    contentType: 'application/json; charset=utf-8'
  })
}

const reportable = (function () {
  let errorsReported = 0

  return function (event) {
    const {lineno, error, filename} = event.originalEvent
    const errorStack = error ? error.stack : undefined
    // Only report errors if we can get backtraces & line numbers
    if (!errorStack || !lineno) {
      return false
    }

    // Ignore errors raised when browsers cancel loading resources after
    // links are clicked or the page is stopped.
    if (unloaded) {
      return false
    }

    if (isChromeExtension(filename)) {
      return false
    }

    // Report a max of 10 errors per user per page load. This way if something is
    // generating errors continuously, we won't flood Haystack with duplicates.
    if (errorsReported >= 10) {
      return false
    }
    errorsReported++

    return true
  }
})()

const extensionScripts = () =>
  (() => {
    const result = []
    for (const script of $('script')) {
      if (/^(?:chrome-extension|file):/.test(script.src)) {
        result.push(script.src)
      }
    }
    return result
  })()

// Store information about the current event in the failbot context so it will
// be included in needles generated while dispatching this event.
const {dispatch} = $.event
$.event.dispatch = function (event) {
  if (event.type === 'error' && event.target === window) {
    // TODO: Doesnt seem like we call this method directly in the code base, ignoring for now
    // till we can investigate
    // eslint-disable-next-line prefer-rest-params
    return dispatch.apply(this, arguments)
  }

  contexts.push({eventType: event.type, eventTarget: event.target})
  // eslint-disable-next-line prefer-rest-params
  const result = dispatch.apply(this, arguments)
  contexts.pop()
  return result
}

// Add the just-completed navigation to session storage to be included in
// needles for debugging.
function pushNavigation(loadType, info) {
  const navigations = getNavigations()
  navigations.push({type: loadType, url: window.location.href, state: window.history.state, info})
  return setNavigations(navigations)
}

const NAVIGATIONS_KEY = 'navigations'

// Retrieve all past navigations from the current session.
function getNavigations() {
  const json = (() => {
    try {
      return sessionStorage.getItem(NAVIGATIONS_KEY)
    } catch (error) {
      return
    }
  })()
  if (json) {
    return JSON.parse(json)
  } else {
    return []
  }
}

// Store the current session's navigations.
function setNavigations(navigations) {
  try {
    return sessionStorage.setItem(NAVIGATIONS_KEY, JSON.stringify(navigations))
  } catch (error) {
    return
  }
}

// Record all navigations in this session for inclusion in needles.
pushNavigation('load')
$(window).on('hashchange', event => pushNavigation('hashchange', {oldURL: event.oldURL, newURL: event.newURL}))
$(window).on('popstate', event => pushNavigation('popstate', {eventState: event.state}))
$(document).on('pjax:success', () => pushNavigation('pjax:success'))
$(document).on('pjax:popstate', event =>
  pushNavigation('pjax:popstate', {pjaxDirection: event.direction, pjaxState: event.state})
)

export default reportErrors
